import { useMutation } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTagGroupDetail, useTags } from '@/hooks';
import { updateTagGroup } from '@/api/tag-group-api';
import { ArrowLeft, Building, Lock, Tag } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLink } from '@/components/ui/app-link';
import { toast } from 'sonner';
import { getAxiosErrorMessage } from '@/utils/error-utils';
import { ownerScopeToInputScope } from '@/lib/owner-scope';
import { TagGroupFormValues, Work4MeTagGroupForm } from './Work4MeTagGroupForm';

export function Work4MeTagGroupDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const parsedGroupId = Number(id);
  const isValidGroupId = Number.isInteger(parsedGroupId) && parsedGroupId > 0;
  const detailQuery = useTagGroupDetail(isValidGroupId ? parsedGroupId : 0);
  const group = detailQuery.data;
  const tagsQuery = useTags();
  const tagsInGroup = (tagsQuery.data ?? []).filter((tag) => tag.tagGroupId === parsedGroupId);
  const updateMutation = useMutation({
    mutationFn: ({ mutId, data }: { mutId: number; data: { name: string; description: string | null; selectionMode: 'SINGLE' | 'MULTI'; groupOrder?: number } }) =>
      updateTagGroup(mutId, data),
    onSuccess: () => {
      toast.success('Saved tag group.');
      detailQuery.refetch();
      tagsQuery.refetch();
    },
    onError: (error) => {
      toast.error(getAxiosErrorMessage(error) ?? 'Failed to update tag group.');
    },
  });

  const initialValues: TagGroupFormValues | null = group
    ? {
      name: group.name,
      description: group.description ?? '',
      scope: ownerScopeToInputScope(group.ownerScope),
      selectionMode: group.selectionMode,
      groupOrder: group.groupOrder ?? undefined,
    }
    : null;

  if (!isValidGroupId) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Invalid tag group id.</p>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-muted-foreground">Loading tag group...</p>
      </div>
    );
  }

  if (detailQuery.isError || !group) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Unable to load tag group detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Tag Group: {group.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{group.selectionMode}</Badge>
              <Badge variant="outline">{group.ownerScope}</Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/tags')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tags
              </Button>
            </div>
          </div>
        </div>
      </div>

      {initialValues ? (
        <div className="px-4 pt-4 pb-2">
          <Work4MeTagGroupForm
            initialValues={initialValues}
            isSubmitting={updateMutation.isPending}
            submitLabel="Save Group"
            submittingLabel="Saving..."
            showScopeSelector={false}
            onSubmit={async (values) => {
              await updateMutation.mutateAsync({
                mutId: group.id,
                data: {
                  name: values.name,
                  description: values.description === '' ? null : values.description,
                  selectionMode: values.selectionMode,
                  groupOrder: values.groupOrder,
                },
              });
            }}
          />
          <div className="grid gap-3 sm:grid-cols-3 pt-4">
            <div className="rounded-md border p-3 sm:col-span-2">
              <p className="text-xs upperanchor tracking-wide text-muted-foreground">Description</p>
              <p className="mt-1 text-sm">{group.description?.trim() || 'No description'}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs upperanchor tracking-wide text-muted-foreground">Scope</p>
              <p className="mt-1 text-sm inline-flex items-center gap-1">
                {group.ownerScope === 'HUB' ? <Building className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {group.ownerScope}
              </p>
            </div>
          </div>

          <div className="rounded-md border p-3 mt-4">
            <p className="text-xs upperanchor tracking-wide text-muted-foreground mb-2">
              Tags in this group ({tagsInGroup.length})
            </p>
            {tagsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading tags...</p>
            ) : tagsInGroup.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tags in this group yet.</p>
            ) : (
              <ul className="space-y-1">
                {tagsInGroup.map((tag) => (
                  <li key={tag.id}>
                    <AppLink
                      to={`/work4me/tags/${tag.id}`}
                      className="text-sm inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Tag className="h-4 w-4" />
                      {tag.name}
                    </AppLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
